import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons'
import { formatDateDisplay, maskUsDateInput, toIsoDateOnly } from '../utils/dateInput'
import 'cally'

type DaisyDatePickerProps = {
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  size?: 'sm' | 'md'
  trailing?: React.ReactNode
  'aria-label'?: string
}

type PopupPos = { top: number; left: number }

/**
 * DaisyUI-styled Cally date picker with a typeable input + calendar button.
 */
export function DaisyDatePicker({
  value,
  onChange,
  disabled = false,
  placeholder = 'MM/DD/YYYY',
  className = '',
  size = 'sm',
  trailing,
  'aria-label': ariaLabel = 'Pick a date',
}: DaisyDatePickerProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<PopupPos | null>(null)
  const [focused, setFocused] = useState(false)
  const [textValue, setTextValue] = useState(() => formatDateDisplay(value))
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popupRef = useRef<HTMLDivElement | null>(null)
  const calendarHostRef = useRef<HTMLDivElement | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const popupId = useId()
  const dateValue = String(value || '').trim().slice(0, 10)
  const compact = size === 'sm'

  useEffect(() => {
    if (focused) return
    setTextValue(formatDateDisplay(value))
  }, [value, focused])

  const commitTypedValue = useCallback(() => {
    const parsed = toIsoDateOnly(textValue)
    if (parsed === null) {
      setTextValue(formatDateDisplay(value))
      return
    }
    if (parsed !== dateValue) onChangeRef.current(parsed)
    setTextValue(formatDateDisplay(parsed))
  }, [textValue, value, dateValue])

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const popupHeight = popupRef.current?.offsetHeight ?? 320
    const popupWidth = popupRef.current?.offsetWidth ?? 300
    const gap = 6
    const spaceBelow = window.innerHeight - rect.bottom - gap
    const openUp = spaceBelow < popupHeight && rect.top > spaceBelow
    const top = openUp ? rect.top - gap - popupHeight : rect.bottom + gap
    let left = rect.left
    left = Math.min(left, window.innerWidth - popupWidth - 8)
    left = Math.max(8, left)
    setPos({
      top: Math.max(8, Math.min(top, window.innerHeight - popupHeight - 8)),
      left,
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    updatePosition()
    const onWin = () => updatePosition()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onPointer = (e: MouseEvent | PointerEvent) => {
      const t = e.target as Node | null
      if (!t) return
      if (triggerRef.current?.contains(t)) return
      if (popupRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer, true)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const host = calendarHostRef.current
    if (!host) return
    const el = host.querySelector('calendar-date') as (HTMLElement & { value?: string }) | null
    if (!el) return

    if (dateValue) el.value = dateValue

    const handleChange = () => {
      const next = String(el.value || '').trim().slice(0, 10)
      onChangeRef.current(next)
      setTextValue(formatDateDisplay(next))
      setOpen(false)
    }

    el.addEventListener('change', handleChange)
    requestAnimationFrame(() => updatePosition())
    return () => el.removeEventListener('change', handleChange)
  }, [open, dateValue, updatePosition])

  return (
    <>
      <div className={`join ${className || 'w-44'}`}>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          maxLength={10}
          className={`input input-bordered join-item w-full ${compact ? 'input-sm' : ''} ${disabled ? 'input-disabled' : ''}`}
          value={textValue}
          placeholder={placeholder}
          aria-label={ariaLabel}
          disabled={disabled}
          readOnly={disabled}
          onFocus={() => setFocused(true)}
          onChange={(e) => {
            const next = maskUsDateInput(e.target.value)
            setTextValue(next)
            const parsed = toIsoDateOnly(next)
            if (parsed) onChangeRef.current(parsed)
          }}
          onBlur={() => {
            setFocused(false)
            commitTypedValue()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitTypedValue()
              ;(e.target as HTMLInputElement).blur()
            }
          }}
        />
        <button
          ref={triggerRef}
          type="button"
          className={`btn btn-square join-item ${compact ? 'btn-sm' : ''}`}
          aria-label={`Open calendar for ${ariaLabel}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? popupId : undefined}
          disabled={disabled}
          onClick={() => {
            if (disabled) return
            setOpen((v) => !v)
          }}
        >
          <FontAwesomeIcon icon={faCalendarDays} />
        </button>
        {trailing}
      </div>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popupRef}
              id={popupId}
              role="dialog"
              aria-label={ariaLabel}
              className="rounded-box border border-base-300 bg-base-100 p-1 shadow-xl"
              style={{
                position: 'fixed',
                top: pos?.top ?? -9999,
                left: pos?.left ?? -9999,
                zIndex: 9999,
                visibility: pos ? 'visible' : 'hidden',
              }}
            >
              <div ref={calendarHostRef}>
                {React.createElement(
                  'calendar-date',
                  {
                    class: 'cally bg-base-100 rounded-box',
                    value: dateValue || undefined,
                  },
                  React.createElement(
                    'svg',
                    {
                      'aria-label': 'Previous',
                      className: 'fill-current size-4',
                      slot: 'previous',
                      xmlns: 'http://www.w3.org/2000/svg',
                      viewBox: '0 0 24 24',
                    },
                    React.createElement('path', { d: 'M15.75 19.5 8.25 12l7.5-7.5' }),
                  ),
                  React.createElement(
                    'svg',
                    {
                      'aria-label': 'Next',
                      className: 'fill-current size-4',
                      slot: 'next',
                      xmlns: 'http://www.w3.org/2000/svg',
                      viewBox: '0 0 24 24',
                    },
                    React.createElement('path', { d: 'm8.25 4.5 7.5 7.5-7.5 7.5' }),
                  ),
                  React.createElement('calendar-month'),
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

export default DaisyDatePicker
